import { ChangeEvent, FormEvent, ReactNode, useEffect, useState } from 'react'
import { DialogTrigger, MenuTrigger } from 'react-aria-components'
import { Outlet, useParams } from 'react-router'
import { ObjectProperties } from '../../shared/types'
import { useHasAccountPermission } from '../account-permissions'
import {
	ObjectTypes,
	useObjectActionMutation,
	useObjectCreate,
	useObjectList,
	useObjectRemove,
} from '../api/object'
import { Button, ButtonLink } from '../components/button'
import { Dialog } from '../components/dialog'
import { Menu, MenuItem, Popover } from '../components/menu'
import { Paper } from '../components/paper'
import { Sidepanel } from '../components/sidepanel'
import { DEFAULT_TABLE_LAYOUT, useTableLayout } from '../layouts'
import { FormLayout } from '../layouts/form-layout'
import { FormLayout as FormLayoutT, TableLayout, TableMultiaction } from '../layouts/types'
import { List, useList } from '../scaffolding/list'
import { ListHeader } from '../scaffolding/list-header'
import { useToggle } from '../use-toggle'
import { FilteringSidepanel } from './filtering-sidepanel'
import { FORMATTER, getFormatter } from './formatters'
import { ObjectAggregate } from './object-aggregate'
import { ObjectForm } from './object-form'
import { Views } from './views'

type ObjectsTableProps = {
	table: TableLayout
	object: keyof ObjectTypes
	related?: ObjectProperties
	children?:
		| ReactNode
		| ((props: {
				selected: Array<string>
				list: Array<ObjectTypes[keyof ObjectTypes]>
		  }) => ReactNode)
	defaultSelected?: Array<string>
}

export function ObjectsTable({
	table,
	object,
	related,
	children,
	defaultSelected,
}: ObjectsTableProps) {
	const [params, setParams] = useList()
	const [filter, setFilter] = useState<Omit<ObjectProperties, 'viewId'> & { viewId?: string }>({})
	const { viewId, ...rest } = filter

	const query = useObjectList(object, { ...params, ...rest, ...related })
	const remove = useObjectRemove(object)
	const canEdit = useHasAccountPermission(table.permissions.edit)
	const canDelete = useHasAccountPermission(table.permissions.delete)

	const [createRelation, toggleCreateRelation] = useToggle()
	const [multiaction, setMultiaction] = useState<TableMultiaction>()
	const [selected, setSelected] = useState<Array<string>>(defaultSelected ?? [])

	const areAllSelected =
		query.data?.list.length === selected.length &&
		query.data.list.every(item => selected.includes(item.id))

	const onSelectAll = (event: ChangeEvent<HTMLInputElement>) => {
		if (event.currentTarget.checked) {
			setSelected(query.data?.list?.map(item => item.id) ?? [])
		} else {
			setSelected([])
		}
	}

	const onSelect = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.currentTarget
		setSelected(selected => {
			if (selected.includes(value)) {
				return selected.filter(id => id !== value)
			} else {
				return [...selected, value]
			}
		})
	}

	useEffect(() => {
		const list = query.data?.list.map(item => item.id) ?? []
		setSelected(selected => {
			return selected.filter(id => list.includes(id))
		})
	}, [query.data])

	const onRemove = (id: string) => {
		if (confirm(`Opravdu chcete smazat ${table.name.singular}?`)) {
			remove.mutate(id)
		}
	}

	const onSetView = (viewId?: string, filter?: ObjectProperties) => {
		setFilter({ ...filter, viewId })
	}

	return (
		<Paper>
			<div className="flex items-center justify-between gap-4 px-3 py-3 sm:px-5 sm:py-4">
				<h3 className="text-md font-bold">{table.name.plural}</h3>
				<div className="flex items-center gap-4">
					{Array.isArray(table.multiactions) && table.multiactions.length ? (
						<MenuTrigger>
							<Button variant="SECONDARY" isDisabled={selected.length === 0}>
								Akce
							</Button>
							<Popover>
								<Menu>
									{table.multiactions.map(action => (
										<MultiactionMenuItem
											key={action.action}
											action={action}
											id={
												action.id
													? (related?.[action.id] as string)
													: undefined
											}
											selected={selected}
											onAction={setMultiaction}
											onDone={() => setSelected([])}
										/>
									))}
								</Menu>
							</Popover>
						</MenuTrigger>
					) : null}
					{multiaction ? (
						<MultiactionForm
							action={multiaction}
							selected={selected}
							related={related as ObjectProperties}
							onClose={() => setMultiaction(undefined)}
							onDone={() => setSelected([])}
						/>
					) : null}
					{table.actions.create ? (
						<ButtonLink variant="PRIMARY" to="./create">
							Vytvořit
						</ButtonLink>
					) : null}
					{table.actions.createRelation ? (
						<Button variant="PRIMARY" onPress={toggleCreateRelation}>
							Vytvořit
						</Button>
					) : null}
					{createRelation ? (
						<CreateRelation
							object={object}
							related={related}
							onClose={toggleCreateRelation}
						/>
					) : null}
					{table.actions.filter ? (
						<DialogTrigger>
							<Button variant="SECONDARY">Filrovat</Button>
							<Sidepanel heading="Filtrovat">
								{({ close }) => (
									<FilteringSidepanel
										object={object}
										filter={filter}
										onClose={close}
										onSubmit={setFilter}
									/>
								)}
							</Sidepanel>
						</DialogTrigger>
					) : null}
					{typeof children === 'function'
						? children({ selected, list: query.data?.list ?? [] })
						: children}
				</div>
			</div>

			{table.actions.filter ? (
				<Views onSetView={onSetView} viewId={viewId} filter={rest} object={object} />
			) : null}
			<List
				params={params}
				setParams={setParams}
				query={query}
				header={
					<tr>
						{table.multiactions ? (
							<th>
								<input
									type="checkbox"
									checked={areAllSelected}
									onChange={onSelectAll}
								/>
							</th>
						) : null}
						{table.columns.map(column => {
							if (column.sortable) {
								return (
									<ListHeader
										key={column.key}
										sortBy={column.key}
										type={column.type}
									>
										{column.label}
									</ListHeader>
								)
							}
							return <th key={column.key}>{column.label}</th>
						})}
						{hasActionColumn(table) ? <th></th> : null}
					</tr>
				}
				footer={
					table.aggregate ? (
						<ObjectAggregate object={object} related={related}>
							{row => (
								<tr key={row.key}>
									{table.multiactions ? <th /> : null}
									<th>Suma</th>
									<th className="text-right">
										{FORMATTER[table.aggregate!.type](row.total)}
									</th>
									<th
										colSpan={
											table.columns.length - (hasActionColumn(table) ? 1 : 2)
										}
									/>
								</tr>
							)}
						</ObjectAggregate>
					) : null
				}
			>
				{item => (
					<tr key={item.id}>
						{table.multiactions ? (
							<td>
								<input
									type="checkbox"
									name="multiselect"
									value={item.id}
									checked={selected.includes(item.id)}
									onChange={onSelect}
								/>
							</td>
						) : null}
						{table.columns.map(column => {
							const { type } = column
							const renderer = getFormatter(column)
							return (
								<td
									width={column.size}
									key={column.key}
									className={type === 'currency' ? 'text-right' : undefined}
								>
									{renderer(item[column.key], column, item, object)}
								</td>
							)
						})}
						{hasActionColumn(table) ? (
							<td width="20%" className="space-x-2">
								{table.actions.view ? (
									<ButtonLink
										variant="SECONDARY"
										size="SMALL"
										to={`./${item.id}`}
									>
										Detail
									</ButtonLink>
								) : null}
								{table.permissions.edit ? (
									<ButtonLink
										isDisabled={!canEdit}
										variant="SECONDARY"
										size="SMALL"
										to={
											table.base === 'root'
												? `/svc/${object}/${item.id}`
												: `${table.base ?? ''}${item.id}`
										}
									>
										Upravit
									</ButtonLink>
								) : null}
								{table.permissions.delete ? (
									<Button
										isDisabled={!canDelete}
										variant="DANGER"
										size="SMALL"
										onPress={() => onRemove(item.id)}
									>
										Smazat
									</Button>
								) : null}
							</td>
						) : null}
					</tr>
				)}
			</List>

			<Outlet />
		</Paper>
	)
}

function CreateRelation({
	object,
	related,
	onClose,
}: {
	object: keyof ObjectTypes
	related?: ObjectProperties
	onClose: VoidFunction
}) {
	const { mutate, isPending } = useObjectCreate(object)
	const onSubmit = (event: FormEvent<HTMLFormElement>) => {
		const form = event.currentTarget
		event.preventDefault()
		mutate(new FormData(form), {
			onSuccess: () => {
				onClose()
			},
		})
	}
	return (
		<Dialog heading="Nový záznam" isOpen onOpenChange={onClose}>
			<div className="m-4">
				<ObjectForm
					object={object}
					data={related}
					onSubmit={onSubmit}
					isPending={isPending}
				/>
			</div>
		</Dialog>
	)
}

function MultiactionMenuItem({
	id,
	action,
	selected,
	onAction,
	onDone,
}: {
	id?: string
	action: TableMultiaction
	selected: Array<string>
	onAction: (action: TableMultiaction, selected: Array<string>) => void
	onDone: VoidFunction
}) {
	const { mutateAsync } = useObjectActionMutation(
		action.object,
		id ?? selected.join(','),
		action.action,
		action.invalidateQueries,
	)

	const onClick = () => {
		if (action.formLayout) {
			onAction(action, selected)
		} else {
			mutateAsync({ ids: selected }).then(onDone)
		}
	}

	return <MenuItem onAction={onClick}>{action.label}</MenuItem>
}

function MultiactionForm({
	action,
	selected,
	related,
	onClose,
	onDone,
}: {
	action: TableMultiaction
	selected: Array<string>
	related: ObjectProperties
	onClose: VoidFunction
	onDone: VoidFunction
}) {
	const id = action.id ? (related[action.id] as string) : selected.join(',')
	const { mutate, isPending } = useObjectActionMutation(
		action.object,
		id,
		action.action,
		action.invalidateQueries,
	)

	const onSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault()
		const formData = new FormData(event.currentTarget)
		const payload: Record<string, unknown> = Object.fromEntries(formData)
		payload.ids = selected
		mutate(payload, {
			onSuccess() {
				onClose()
				onDone()
			},
		})
	}

	return (
		<Dialog heading="Nový záznam" isOpen onOpenChange={onClose}>
			<div className="m-4">
				<form className="space-y-4 m-auto max-w-[60em]" onSubmit={onSubmit}>
					<FormLayout layout={action.formLayout as FormLayoutT} data={related} />
					<Button type="submit" isPending={isPending} isDisabled={isPending}>
						Uložit
					</Button>
				</form>
			</div>
		</Dialog>
	)
}

const hasActionColumn = (table: TableLayout) => {
	return !!table.permissions.edit || !!table.permissions.delete || !!table.actions.view
}

type ObjectsWitHConfigProps = {
	config?: TableLayout
	name: string
} & Omit<ObjectsTableProps, 'table'>

export function ObjectsWithConfig({ config, object, name, ...props }: ObjectsWitHConfigProps) {
	const { layout, isLoading } = useTableLayout(object, name, { enabled: !config })
	const table = config ?? layout ?? DEFAULT_TABLE_LAYOUT

	if (isLoading) return <div>Loading...</div>
	if (!table) throw new Error('No table configuration available')

	return <ObjectsTable table={table} object={object} {...props} />
}

export function Objects({ related }: { related?: ObjectProperties }) {
	const { object } = useParams<{ object: keyof ObjectTypes }>()

	if (!object) throw new Error('Invalid invocation')

	return <ObjectsWithConfig object={object} name="main" related={related} />
}
